import { assoc, concat, CorrectLens, curry, map, view } from "ramda";
import {
  MapNode,
  mapTreeChildren,
  ReduceNode,
  reduceTreeChildren
} from "../../../utils/mapReduceTree";
import { AccountId, Spread, SpreadAccount } from "../spread/types";
import {
  spreadBalanceSheetTopLevelChildren,
  spreadCashFlowTopLevelChildren,
  spreadIncomeStatementTopLevelChildren
} from "./lenses";
import { AccountMap } from "./types";

interface AccountHierarchyNode {
  children: AccountId[];
  ancestors: AccountId[];
}

export const rootNode: unique symbol = Symbol();

export type PartialAccountHierarchy = AccountMap<AccountHierarchyNode>;

export type AccountHierarchy = PartialAccountHierarchy & {
  [rootNode]: AccountHierarchyNode;
};

const getIds = map((child: SpreadAccount) => child.id);

interface AccountWithAncestors {
  account: SpreadAccount;
  ancestors: AccountId[];
}

export const buildAccountHierarchy = curry(
  (
    topLevelChildLens: CorrectLens<Spread, SpreadAccount[]>,
    spread: Spread | undefined
  ): AccountHierarchy => {
    if (!spread) {
      return { [rootNode]: { ancestors: [], children: [] } };
    }
    const topLevelChildren: SpreadAccount[] = view(topLevelChildLens, spread);
    const topLevelChildrenWithAncestors = map(
      (account): AccountWithAncestors => ({
        account,
        ancestors: []
      }),
      topLevelChildren
    );
    const partialHiearchy = reduceTreeChildren<
      AccountWithAncestors,
      PartialAccountHierarchy
    >(
      ({ account, ancestors }) => {
        const newAncestors = concat(ancestors, [account.id]);
        return map(
          child => ({ account: child, ancestors: newAncestors }),
          account.children
        );
      },
      (parialAccountHierarchy, { account, ancestors }) =>
        assoc(
          account.id,
          { children: getIds(account.children), ancestors },
          parialAccountHierarchy
        ),
      {}
    )(topLevelChildrenWithAncestors);
    return {
      [rootNode]: { ancestors: [], children: getIds(topLevelChildren) },
      ...partialHiearchy
    };
  }
);

export const buildIncomeStatementAccountHierarchy = buildAccountHierarchy(
  spreadIncomeStatementTopLevelChildren
);
export const buildBalanceSheetAccountHierarchy = buildAccountHierarchy(
  spreadBalanceSheetTopLevelChildren
);
export const buildCashFlowAccountHierarchy = buildAccountHierarchy(
  spreadCashFlowTopLevelChildren
);

export const getChildren = curry(
  (accountHierarchy: AccountHierarchy, accountId: AccountId) =>
    accountHierarchy[accountId].children
);

export const getAncestors = curry(
  (accountHierarchy: AccountHierarchy, accountId: AccountId) =>
    accountHierarchy[accountId].ancestors
);

export const mapAccountHierarchy = <MappedAccountType>(
  accountHierarchy: AccountHierarchy,
  mapAccount: MapNode<AccountId, MappedAccountType>
) =>
  mapTreeChildren(getChildren(accountHierarchy), mapAccount)(
    accountHierarchy[rootNode].children
  );

export const reduceAccountHierarchy = <ReducedType>(
  accountHierarchy: AccountHierarchy,
  reduceAccounts: ReduceNode<AccountId, ReducedType>,
  initialValue: ReducedType
) =>
  reduceTreeChildren(
    getChildren(accountHierarchy),
    reduceAccounts,
    initialValue
  )(accountHierarchy[rootNode].children);



// WEBPACK FOOTER //
// ./src/spread/model/derivedModel/accountHierarchy.ts